/**
 * EmergencyView - Emergency alert system
 * Sends emergency notification to family
 */
import React, { useState } from 'react';
import axios from 'axios';
import { AlertCircle, Phone, Loader } from 'lucide-react';
import { toast } from 'react-hot-toast';

const API = process.env.REACT_APP_BACKEND_URL;

export default function EmergencyView({ senior, household, deviceId }) {
  const [isSending, setIsSending] = useState(false);
  const [emergencySent, setEmergencySent] = useState(false);

  const sendEmergency = async () => {
    if (!senior?.id || !household?.id || !deviceId) {
      toast.error('Notfall konnte nicht gesendet werden');
      return;
    }

    try {
      setIsSending(true);
      
      console.log('[Device][Emergency] Sending emergency alert');
      
      // Send emergency notification via new endpoint
      const response = await axios.post(`${API}/api/emergency/device`, {
        device_id: deviceId,
        senior_id: senior.id,
        household_id: household.id,
        timestamp: new Date().toISOString(),
        type: 'emergency',
        message: 'Notfall ausgelöst vom Gerät'
      });
      
      setEmergencySent(true);
      
      const notifiedCount = response.data?.notified_count || 0;
      const message = notifiedCount > 0 
        ? `🚨 NOTFALL GEMELDET! ${notifiedCount} Familienmitglied(er) benachrichtigt.`
        : '🚨 NOTFALL GEMELDET! Ihre Familie wird benachrichtigt.';
      
      toast.error(message, {
        duration: 5000
      });
      
      console.log(`[Device][Emergency] Alert sent successfully, notified=${notifiedCount}`);
      
    } catch (error) {
      console.error('[Device][Emergency] Failed to send:', error);
      toast.error('Notfall konnte nicht gemeldet werden');
    } finally {
      setIsSending(false);
    }
  };

  // Success confirmation screen
  if (emergencySent) {
    return (
      <div className="fixed inset-0 bg-red-600 flex items-center justify-center z-50">
        <div className="text-center text-white p-8">
          <AlertCircle className="w-32 h-32 mx-auto mb-6 animate-pulse" />
          <h1 className="text-6xl font-bold mb-6">
            NOTFALL GEMELDET
          </h1>
          <p className="text-3xl mb-8">
            Ihre Familie wurde benachrichtigt
          </p>
          <div className="bg-white bg-opacity-20 rounded-3xl p-6 mb-8">
            <p className="text-2xl">
              Jemand wird sich bald bei Ihnen melden
            </p>
          </div>
          <button
            onClick={() => setEmergencySent(false)}
            className="px-12 py-6 bg-white text-red-600 rounded-2xl text-2xl font-bold hover:bg-gray-100 transition-all"
          >
            Zurück zum Startbildschirm
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="text-center mb-8">
        <h1 className="text-5xl font-bold text-red-900 mb-4 flex items-center justify-center gap-3">
          <AlertCircle className="w-12 h-12 text-red-600" />
          Notfall
        </h1>
      </div>

      <div className="space-y-6">
        {/* Emergency Button */}
        <div className="text-center">
          <button
            onClick={sendEmergency}
            disabled={isSending}
            className="bg-gradient-to-r from-red-600 to-red-700 hover:from-red-700 hover:to-red-800 disabled:opacity-50 text-white px-16 py-12 rounded-3xl shadow-2xl hover:shadow-3xl transition-all text-4xl font-bold flex flex-col items-center gap-4 mx-auto animate-pulse disabled:animate-none"
          >
            {isSending ? (
              <>
                <Loader className="w-24 h-24 animate-spin" />
                WIRD GESENDET...
              </>
            ) : (
              <>
                <AlertCircle className="w-24 h-24" strokeWidth={3} />
                NOTFALL MELDEN
              </>
            )}
          </button>
        </div>

        {/* Warning Box */}
        <div className="bg-red-50 border-4 border-red-500 rounded-3xl p-8 shadow-lg">
          <h3 className="text-3xl font-bold text-red-900 mb-4 text-center">
            ⚠️ Wichtig
          </h3>
          <ul className="space-y-4 text-2xl text-gray-800">
            <li>• Ihre Familie wird sofort benachrichtigt</li>
            <li>• Nutzen Sie diese Funktion nur in echten Notfällen</li>
            <li>• Bei lebensbedrohlichen Situationen rufen Sie 112 an</li>
          </ul>
        </div>

        {/* Emergency Numbers */}
        <div className="bg-white border-2 border-gray-300 rounded-3xl p-6 shadow-lg">
          <h3 className="text-2xl font-bold text-gray-900 mb-4">
            📞 Notrufnummern
          </h3>
          <div className="space-y-3 text-xl">
            <div className="flex justify-between items-center bg-red-50 p-4 rounded-xl">
              <span className="font-bold">Feuerwehr / Rettungsdienst:</span>
              <span className="font-bold text-red-600 text-3xl">112</span>
            </div>
            <div className="flex justify-between items-center bg-blue-50 p-4 rounded-xl">
              <span className="font-bold">Polizei:</span>
              <span className="font-bold text-blue-600 text-3xl">110</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
